import { useSearchParams } from 'react-router-dom';

import type { FC } from 'react';

import IncidentIcon from '@/assets/icons/incident.svg?react';
import StationIcon from '@/assets/icons/station-list.svg?react';
import { cn } from '@/lib/utils';
import { SEARCH_PARAM_STATION_ID } from '@/utils/constants/queries';

type StationSummaryProps = {
  name: string;
  isDisabled: boolean;
  stationId: string;
  hasIncident: boolean;
};

const StationSummary: FC<StationSummaryProps> = ({
  name,
  isDisabled,
  stationId,
  hasIncident,
}: StationSummaryProps) => {
  const [, setSearchParams] = useSearchParams();

  const handleClick = () => {
    if (isDisabled) {
      return;
    }

    setSearchParams((prev) => {
      prev.set(SEARCH_PARAM_STATION_ID, stationId);

      return prev;
    });
  };

  return (
    <div
      className={cn('flex items-center justify-between gap-2 pb-2.5 pt-0.5', {
        'cursor-pointer': !isDisabled,
      })}
      onKeyDown={(event) => event.stopPropagation()}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      aria-label="station-summary"
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'size-8 flex items-center justify-center border border-secondary rounded-md bg-secondary',
            {
              'bg-secondary/50': isDisabled,
            }
          )}
        >
          <StationIcon
            className={cn('size-5 text-accent-foreground', {
              'text-muted-foreground': isDisabled,
            })}
          />
        </div>
        <div
          className={cn('font-semibold text-card-foreground text-sm', {
            'text-muted-foreground': isDisabled,
          })}
        >
          {name}
        </div>
      </div>
      {hasIncident && (
        <div
          className={cn('flex items-center gap-2 pr-1', {
            'opacity-[.5]': isDisabled,
          })}
        >
          <IncidentIcon className="size-5 fill-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default StationSummary;
